import {DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import {QueryClient, useQueryClient} from '@tanstack/react-query';
import {Button, Form, Input, InputNumber, Modal, Select, Upload} from 'antd';
import ImgCrop from 'antd-img-crop';
import PropTypes from 'prop-types';
import  {useEffect, useState} from 'react';
import toast from 'react-hot-toast';

const ProductEditScreen = ({
  editModal,
  setEditModal,
  form,
  updateProductMutation,
  token,
  dataSource,
  setDataSource,
  categoryOptions,
  brandOptions,
  promoCodeOptions,
  handleModalImageUpload, 
  handleUpdateSubmit,
}) => {
  const [featuresOptions, setFeaturesOptions] = useState([]);
  const [isFeaturesDisabled, setIsFeaturesDisabled] = useState(true);

  const [deletedVariantImages, setDeletedVariantImages] = useState([]);
  const queryClient = useQueryClient();

  const mapUrlsToFiles = (images = []) =>
    images
      .map((img, index) => {
        const url =
        typeof img === 'string'
          ? img
          : img?.url || img?.thumbUrl;

        if (!url) {return null;}

        return {
          uid: `${url}-${index}`,
          name: url.split('/').pop(),
          status: 'done',
          url,
          thumbUrl: url,
        };
      })
      .filter(Boolean);

  useEffect(() => {
    if (!editModal.record) {return;}

    const record = editModal.record;

    form.setFieldsValue({
      productName: record.productName,
      description: record.description,
      price: record.price,
      discount: record.discount,
      category: record.category,
      brand: record.brand,
      promoCode: record.promoCode ?? null,
      eanCode: record.eanCode || '',
      features: record.features || [],
      status: record.status || 'ACTIVE',
      
      variants: (record.variants || []).map(v => ({
        color: v.color.trim().toLowerCase(),
        isExisting: true,
        images: mapUrlsToFiles(v.images || []), 

        sizes: (v.sizes || []).map(s => ({
          size: s.size,
          quantity: s.quantity,
          extraPrice: s.extraPrice || 0,
        })),
      })),
    });
    console.log('VARIANTS FROM API:', editModal.record.variants);

  }, [editModal.record]);

  const handleCategoryChange = categoryValue => {
    form.setFieldsValue({features: []});
    const selectedCategory = categoryOptions.find(c => c.value === categoryValue);
    
    if (selectedCategory?.features) {
      const featureList = Array.isArray(selectedCategory.features) 
        ? selectedCategory.features 
        : selectedCategory.features.features || [];
        
      setFeaturesOptions(featureList.map(f => ({label: f, value: f})));
      setIsFeaturesDisabled(false);
    } else {
      setFeaturesOptions([]);
      setIsFeaturesDisabled(true);
    }
  };

  const toDropboxRawUrl = (url = '') =>
    url.replace('www.dropbox.com', 'dl.dropboxusercontent.com')
      .replace('?dl=0', '');

  const normalizeUploadFileList = (fileList = []) => {
    return fileList.map(file => {
      const rawUrl = file.url
        ? toDropboxRawUrl(file.url)
        : file.thumbUrl
          ? toDropboxRawUrl(file.thumbUrl)
          : file.thumbUrl;

      return {
        ...file,
        url: rawUrl,
        thumbUrl: rawUrl,
      };
    });
  };

  const handleSubmit = async values => {
    try {
      const formData = new FormData();

      const command = {
        productName: values.productName,
        description: values.description,
        price: values.price,
        productType: 'HANDBAG',
        status: values.status,
        discount: values.discount || 0,
        promoCode: values.promoCode ?? '',
        brand: values.brand,
        category: values.category,
        eanCode: values.eanCode,
        features: values.features || [],
        variants: values.variants.map(v => ({
          color: v.color.trim().toLowerCase(),
          sizes: v.sizes.map(s => ({
            size: s.size,
            quantity: s.quantity,
            extraPrice: s.extraPrice || 0,
          })),
        })),
        deletedVariantImages,
      };

      formData.append('command', JSON.stringify(command));

      values.images?.forEach((file, index) => {
        if (!file.originFileObj) {return;}
        const ext = file.originFileObj.name.split('.').pop();
        formData.append(
          'files',
          new File([file.originFileObj], `main_${index + 1}.${ext}`, {
            type: file.originFileObj.type,
          }),
        );
      });

      values.variants.forEach(variant => {
        const color = variant.color.trim().toLowerCase();
        variant.images?.forEach((file, index) => {
          if (!file.originFileObj) {return;}
          const ext = file.originFileObj.name.split('.').pop();
          formData.append(
            'files',
            new File([file.originFileObj], `${color}_${index + 1}.${ext}`, {
              type: file.originFileObj.type,
            }),
          );
        });
      });

      await updateProductMutation.mutateAsync({
        id: editModal.record.id,
        token,
        formData,
      });

      queryClient.invalidateQueries(['products']); 

      toast.success('Product updated successfully');
      setEditModal({visible: false, record: null});

    } catch (err) {
      console.error(err);
      // error toast should ideally be in mutation onError
      toast.error(err.message || 'Product update failed');
    }
  };
  return (
    <Modal
      title={'Edit Product'}
      open={editModal.visible}
      width={900}
      onCancel={() => setEditModal({visible: false, record: null})}
      onOk={() => form.submit()}     
      confirmLoading={updateProductMutation.isLoading}
    >
      <Form form={form} layout={'vertical'} onFinish={handleSubmit}>
        <div className={'flex gap-4'}>
          <Form.Item
            name={'eanCode'}
            className={'w-1/4'}
            label={'EAN Code'}
            rules={[{required: true, message: 'EAN code is required'}]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name={'productName'}
            className={'w-3/4'}
            label={'Product Name'}
            rules={[{required: true}]}
          >
            <Input />
          </Form.Item>
        </div>

        <Form.Item
          name={'description'}
          label={'Description'}
          rules={[{required: true}]}
        >
          <Input.TextArea rows={3} />
        </Form.Item>

        <div className={'flex md:flex-row flex-col md:gap-4'}>
          <Form.Item name={'status'} label={'Status'} className={'md:w-1/3'}>
            <Select
              options={[
                {label: 'Active', value: 'ACTIVE'},
                {label: 'Inactive', value: 'INACTIVE'},
              ]}
            />
          </Form.Item>
          <Form.Item name={'category'} label={'Category'} rules={[{required: true}]} className={'md:w-1/3'}>
            <Select options={categoryOptions} onChange={handleCategoryChange} />
          </Form.Item>
        
          <Form.Item name={'brand'} label={'Brand'} rules={[{required: true}]} className={'md:w-1/3'}>
            <Select options={brandOptions} />
          </Form.Item>
        </div>

        <Form.Item name={'features'} label={'Features'}>
          <Select
            mode={'multiple'}
            disabled={isFeaturesDisabled}
            options={featuresOptions}
          />
        </Form.Item>

        <div className={'flex gap-2'}>
          <Form.Item
            name={'price'}
            label={'Base Price'}
            className={'w-1/3'}
            rules={[{required: true}]}
          >
            <InputNumber min={0} className={'w-full'} />
          </Form.Item>

          <Form.Item
            name={'discount'}
            label={'Discount (%)'}
            className={'w-1/3'}
          >
            <InputNumber min={0} max={100} className={'w-full'} />
          </Form.Item>

          <Form.Item
            name={'promoCode'}
            label={'Promo Code'}
            className={'w-1/3'}
          >
            <Select
              allowClear
              placeholder={'Select promo code'}
              options={promoCodeOptions}
            />
          </Form.Item>
        </div>

        <Form.List name={'variants'}>
          {(fields, {add, remove}) => (
            <>
              {fields.map(({key, name, ...restField}) => (
                <div key={key} className={'border border-gray-200 rounded p-4 mb-4'}>
                  <Form.Item 
                    {...restField}
                    name={[name, 'color']} 
                    label={'Color'} 
                    rules={[{required: true}]}
                    
                  >
                    <Input
                      placeholder={'e.g. BLUE'}
                      disabled={form.getFieldValue(['variants', name, 'isExisting'])}
                      onChange={e => {
                        const value = e.target.value.toUpperCase();

                        form.setFieldValue(['variants', name, 'color'], value);
                      }}
                    />

                  </Form.Item>
 
                  <Form.Item
                    label={'Variant Images'}
                  >
                    <ImgCrop rotationSlider>
                      <Upload
                        listType={'picture-card'}
                        multiple
                        beforeUpload={() => false} 
                        accept={'image/*'}
                        showUploadList={{showRemoveIcon: false}}
                        fileList={normalizeUploadFileList(
                          form.getFieldValue(['variants', name, 'images']) || [],
                        )}

                        onChange={({fileList}) => {
                          form.setFieldValue(['variants', name, 'images'], fileList);
                        }}
                        onRemove={file => {
                          const current = form.getFieldValue(['variants', name, 'images']) || [];
                          const updated = current.filter(f => f.uid !== file.uid);
                          form.setFieldValue(['variants', name, 'images'], updated);
                        }}
                      >
                        <PlusOutlined />
                      </Upload>
                    </ImgCrop>
                  </Form.Item>

                  <Form.List name={[name, 'sizes']}>
                    {(sizeFields, {add: addSize, remove: removeSize}) => (
                      <>
                        {sizeFields.map(({key: sKey, name: sName}) => (
                          <div key={sKey} className={'flex md:flex-row flex-col md:gap-2 md:items-center border border-primary-100 rounded-md p-4  mb-4'}>
                            <Form.Item className={'flex mb-0 md:mb-5'} name={[sName, 'size']} label={'Size'}>
                              <Input />
                            </Form.Item>
                 
                            <Form.Item className={'flex mb-0 md:mb-5'} name={[sName, 'extraPrice']} label={'Extra Price'}>
                              <InputNumber min={0} />
                            </Form.Item>
                 
                            <Form.Item
                              className={'flex mb-0 md:mb-5'}
                              name={[sName, 'quantity']}
                              label={'Quantity'}
                              rules={[{required: true}]}
                            >
                              <InputNumber min={1} />
                            </Form.Item>
                 
                            {sizeFields.length > 1 && (
                              <Button className={'w-12 md:mt-2'} danger onClick={() => removeSize(sName)}>
                                <DeleteOutlined />
                              </Button>
                            )}
                          </div>
                        ))}
                 
                        <Button
                          type={'dashed'}
                          className={'text-primary-800font-medium'}
                          block
                          onClick={() => addSize({size: '', quantity: 1, extraPrice: 0})}
                        >
                                           + Add Size
                        </Button>
                      </>
                    )}
                  </Form.List>
                 
                  {fields.length > 1 && (
                    <Button className={'mt-4'} danger icon={<DeleteOutlined />} onClick={() => remove(name)}>
                                       Remove Variant
                    </Button>
                  )}
                </div>
              ))}
                 
              <Button 
                className={'bg-primary-50 text-primary-800 font-medium text-base'}
                type={'dashed'} block onClick={() =>
                  add({
                    color: '',
                    isExisting: false,
                    images: [],
                    sizes: [{size: '', quantity: 1, extraPrice: 0}], 
                  })
                }
              >
                                 + Add Color Variant
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

ProductEditScreen.propTypes = {
  editModal: PropTypes.object.isRequired,
  setEditModal: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  updateProductMutation: PropTypes.object.isRequired,
  token: PropTypes.string.isRequired,
  dataSource: PropTypes.array.isRequired,
  setDataSource: PropTypes.func.isRequired,
  categoryOptions: PropTypes.array.isRequired,
  brandOptions: PropTypes.array.isRequired,
  promoCodeOptions: PropTypes.array.isRequired,
  handleModalImageUpload: PropTypes.func.isRequired,
  handleUpdateSubmit:PropTypes.func.isRequired,
};

export default ProductEditScreen;
